class WrapgramDeviceContext {
  constructor() {
    this.BRAND_KEY = 'wrapgram_brand';
    this.MODEL_KEY = 'wrapgram_model';
    this.LAYOUT_KEY = 'wrapgram_layout';
    this.CONTEXT_TIMESTAMP_KEY = 'wrapgram_context_ts';
    this.CONTEXT_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days
  }

  // Initialize context from localStorage. Purges stale (>30 day) context here so every
  // page enforces the same expiry consistently, not just whichever page happens to check it.
  init() {
    try {
      const timestamp = localStorage.getItem(this.CONTEXT_TIMESTAMP_KEY);
      if (timestamp) {
        const age = Date.now() - parseInt(timestamp, 10);
        if (isNaN(age) || age > this.CONTEXT_TTL) {
          this.clearContext();
          return this.context;
        }
      }

      const brand = this.getBrand();
      const model = this.getModel();
      const layout = this.getLayout();

      this.context = { brand, model, layout, isActive: !!(brand && model) };
      return this.context;
    } catch (error) {
      console.error('[Wrapgram] Context init failed:', error);
      this.clearContext();
      return { brand: null, model: null, layout: 'layout-1', isActive: false };
    }
  }

  // Get brand from localStorage
  getBrand() {
    try {
      return localStorage.getItem(this.BRAND_KEY) || null;
    } catch {
      return null;
    }
  }

  // Get model from localStorage
  getModel() {
    try {
      return localStorage.getItem(this.MODEL_KEY) || null;
    } catch {
      return null;
    }
  }

  // Get layout from localStorage with fallback
  getLayout() {
    try {
      return localStorage.getItem(this.LAYOUT_KEY) || 'layout-1';
    } catch {
      return 'layout-1';
    }
  }

  // Set device context (brand + model)
  setDevice(brand, model, layout) {
    // Validate inputs
    if (!brand || !model || !layout) {
      console.warn('[Wrapgram] Invalid device parameters');
      return false;
    }

    try {
      // Store in localStorage
      localStorage.setItem(this.BRAND_KEY, String(brand).trim());
      localStorage.setItem(this.MODEL_KEY, String(model).trim());
      localStorage.setItem(this.LAYOUT_KEY, String(layout).trim());
      localStorage.setItem(this.CONTEXT_TIMESTAMP_KEY, Date.now().toString());

      // Update in-memory context
      this.context = { brand, model, layout, isActive: true };

      // Dispatch event
      this.dispatchContextChanged();
      return true;
    } catch (error) {
      console.error('[Wrapgram] Failed to set device:', error);
      return false;
    }
  }

  // Clear device context
  clearContext() {
    try {
      localStorage.removeItem(this.BRAND_KEY);
      localStorage.removeItem(this.MODEL_KEY);
      localStorage.removeItem(this.LAYOUT_KEY);
      localStorage.removeItem(this.CONTEXT_TIMESTAMP_KEY);
      this.context = { brand: null, model: null, layout: 'layout-1', isActive: false };
      this.dispatchContextCleared();
    } catch (error) {
      console.error('[Wrapgram] Failed to clear context:', error);
    }
  }

  // Check if context is still valid (not expired)
  isContextValid() {
    try {
      const timestamp = localStorage.getItem(this.CONTEXT_TIMESTAMP_KEY);
      if (!timestamp) return false;

      const age = Date.now() - parseInt(timestamp, 10);
      return age < this.CONTEXT_TTL && this.context.isActive;
    } catch {
      return false;
    }
  }

  // Get human-readable device name
  getDeviceDisplayName() {
    if (!this.context.isActive) return null;

    const brand = this.context.brand;
    const model = this.context.model;

    // Look up in brand catalog
    if (window.WrapgramConfig?.brands) {
      const brandObj = window.WrapgramConfig.brands.find(b => b.id === brand);
      if (brandObj) {
        const modelObj = brandObj.models.find(m => m.id === model);
        if (modelObj) {
          return `${brandObj.display_name} ${modelObj.display_name}`;
        }
      }
    }

    return `${brand} ${model}`;
  }

  // Dispatch context changed event
  dispatchContextChanged() {
    const event = new CustomEvent('wrapgram:context-changed', {
      detail: this.context,
      bubbles: true
    });
    window.dispatchEvent(event);
  }

  // Dispatch context cleared event
  dispatchContextCleared() {
    const event = new CustomEvent('wrapgram:context-cleared', {
      bubbles: true
    });
    window.dispatchEvent(event);
  }

  // Get current context
  getContext() {
    return { ...this.context };
  }
}

// Initialize globally
window.WrapgramDeviceContext = new WrapgramDeviceContext();
window.WrapgramDeviceContext.init();
